import { useEffect, useState } from 'react'
import { Grid2x2, Minus, Plus } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { safeAreaBottom } from '../lib/safe-area'
import { CountReasonPicker } from './count-reason-picker'
import type {
  CountItemView,
  CountVarianceReason,
} from '@/api/endpoints/inventory-count-sessions'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  addStockQuantities,
  formatStockDisplay,
  subtractStockQuantities,
} from '@/shared/lib/stock-quantity'

export interface CountItemSheetTarget {
  item: CountItemView
  /**
   * A variance carries a counted quantity and is settled with a reason; an article that was never
   * captured has no quantity yet, so its sheet asks for one — and 0 there means "wirklich nichts
   * da", which the review resolves as confirmed zero without asking why.
   */
  kind: 'variance' | 'notCounted'
}

interface CountItemSheetProps {
  target: CountItemSheetTarget | null
  /** The group's reason, pre-selected so confirming a run of articles stays one tap per article. */
  groupReason: CountVarianceReason | undefined
  onClose: () => void
  /** Hands over to the digit pad for a quantity that is more than a step or two away. */
  onOpenPad: () => void
  /**
   * Settles the article. `qty` is only present when the counter changed it here, in which case the
   * caller recounts before confirming; `reason` is absent for a not-counted article.
   */
  onConfirm: (args: {
    qty?: number
    reason?: CountVarianceReason
    recountConfirmed?: boolean
  }) => void
  /** Ignores the article — offered for a never-captured one only, which is what the API allows. */
  onIgnore: () => void
  pending: boolean
}

const MAX_STEP_QTY = 99_999

/**
 * Everything about one article of the review, in the drawer the counter already knows from
 * counting (CHG-2026-09-inventory-count-review-ux).
 *
 * This is where the per-row controls went when the list rows became list rows: the quantity as a
 * control rather than a label, the reason pills, and the actions. The counted quantity is the
 * point — "the number can't be changed once it's entered" was the complaint, so here it is, with
 * −/+ for the usual one-or-two-off and the full pad one tap away.
 */
export function CountItemSheet({
  target,
  groupReason,
  onClose,
  onOpenPad,
  onConfirm,
  onIgnore,
  pending,
}: CountItemSheetProps) {
  const { t } = useTranslation('inventory-count')
  const [qty, setQty] = useState<number | null>(null)
  const [reason, setReason] = useState<CountVarianceReason | undefined>(
    undefined,
  )

  const item = target?.item ?? null
  const kind = target?.kind ?? 'variance'
  const countedQty = item?.countedQty ?? null

  useEffect(() => {
    if (target) {
      setQty(target.item.countedQty ?? 0)
      setReason(target.item.reason ?? groupReason)
    }
  }, [target, groupReason])

  if (item == null) return null

  // Integer thousandths (CHG-2026-09-stock-usage-and-price-twins): whole numbers come out exactly
  // as before, a partial amount without float noise in what the recount sends.
  const expected = addStockQuantities(
    item.expectedAvailable,
    item.expectedReserved,
  )
  const current = qty ?? 0
  // A never-captured article settled at 0 is "wirklich nichts da": the API's confirm_zero, which
  // writes the expected quantity away and asks for no reason — the same answer today's "Wirklich 0"
  // button gives, reached through the quantity instead of through a button of its own.
  const isZeroConfirm = kind === 'notCounted' && current === 0
  const changed = countedQty == null ? !isZeroConfirm : current !== countedQty
  const delta = subtractStockQuantities(current, expected)
  // The API's surplus gate: more stock than expected is confirmed either after a recount or with
  // an explicit "I counted it again and it stands".
  const needsRecountConfirm = delta > 0 && !item.recounted
  // A variance of zero resolves as MATCHED and carries no reason, here as on the server.
  const reasonRequired = !isZeroConfirm && delta !== 0
  const canConfirm = !pending && (!reasonRequired || reason != null)

  function confirm() {
    onConfirm({
      qty: changed ? current : undefined,
      reason: reasonRequired ? reason : undefined,
      recountConfirmed: needsRecountConfirm ? true : undefined,
    })
  }

  return (
    <Drawer
      open={target != null}
      onOpenChange={(next) => !next && onClose()}
      repositionInputs={false}
    >
      <DrawerContent className="max-h-[95svh]">
        <DrawerHeader className="pb-2 text-left">
          <DrawerTitle className="truncate">{item.name}</DrawerTitle>
          <DrawerDescription className="tabular-nums">
            {kind === 'variance'
              ? t('review.rowDetail', {
                  counted: formatStockDisplay(countedQty ?? 0),
                  expected: formatStockDisplay(expected),
                })
              : t('review.notCountedDetail', {
                  expected: formatStockDisplay(expected),
                })}
          </DrawerDescription>
        </DrawerHeader>

        <div className="flex min-h-0 flex-col gap-4 overflow-y-auto px-4 pb-2">
          <div className="flex flex-col gap-2">
            <p className="text-sm text-muted-foreground">
              {t('review.sheetQuantityLabel')}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                className="h-14 w-14 shrink-0"
                aria-label={t('review.sheetDecrease')}
                disabled={current <= 0}
                onClick={() =>
                  setQty(Math.max(0, subtractStockQuantities(current, 1)))
                }
              >
                <Minus className="h-5 w-5" />
              </Button>
              <Button
                variant="outline"
                className="h-14 flex-1 gap-2 text-2xl font-semibold tabular-nums"
                onClick={onOpenPad}
              >
                {formatStockDisplay(current)}
                <Grid2x2
                  className="h-4 w-4 text-muted-foreground"
                  aria-hidden
                />
              </Button>
              <Button
                variant="outline"
                className="h-14 w-14 shrink-0"
                aria-label={t('review.sheetIncrease')}
                disabled={current >= MAX_STEP_QTY}
                onClick={() => setQty(addStockQuantities(current, 1))}
              >
                <Plus className="h-5 w-5" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {kind === 'notCounted'
                ? t('review.sheetZeroHint')
                : t('review.sheetPadHint')}
            </p>
          </div>

          {reasonRequired && (
            <div className="flex flex-col gap-2">
              <p className="text-sm text-muted-foreground">
                {t('review.sheetReasonLabel')}
              </p>
              <CountReasonPicker value={reason} onChange={setReason} />
            </div>
          )}
        </div>

        <DrawerFooter className="pt-2" style={safeAreaBottom('1rem')}>
          <Button
            size="lg"
            className="h-12"
            disabled={!canConfirm}
            onClick={confirm}
          >
            {canConfirm ? t('review.confirm') : t('review.chooseReason')}
          </Button>
          <div
            className={cn('grid gap-2', kind === 'notCounted' && 'grid-cols-2')}
          >
            {kind === 'notCounted' && (
              <Button
                variant="outline"
                size="lg"
                className="h-12"
                disabled={pending}
                onClick={onIgnore}
              >
                {t('review.ignore')}
              </Button>
            )}
            <Button
              variant="outline"
              size="lg"
              className="h-12"
              onClick={onClose}
            >
              {t('quantitySheet.cancel')}
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
